import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, ShieldAlert, Users, Send, CreditCard, Target, CalendarCheck, MapPin, Plug } from "lucide-react";
import { PageBody, PageHeader } from "@/components/Page";
import { Badge } from "@/components/ui/badge";
import { SetupStrip } from "@/components/SetupStrip";
import api from "@/lib/api";

const ITEMS = [
  { key: "offer", label: "Offer created", icon: Target, to: "/app/offer" },
  { key: "booking_url", label: "Booking URL saved (active workspace)", icon: CalendarCheck, to: "/app/settings/integrations" },
  { key: "physical_address", label: "Physical mailing address saved", icon: MapPin, to: "/app/admin/sending" },
  { key: "sender_connected", label: "A sender connected", icon: Plug, to: "/app/settings/integrations" },
  { key: "seat_free", label: "At least one seat free", icon: Users, to: "/app/admin/team" },
];

export default function AdminOverview() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;

  return (
    <PageBody>
      <PageHeader eyebrow="Admin" title="Overview" subtitle="Account health, readiness checklist, plan and usage for this organization." />

      {!d.live_send && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 mb-6 flex items-center gap-3" data-testid="live-send-banner">
          <ShieldAlert className="h-5 w-5 text-amber-500 shrink-0" />
          <p className="text-sm text-foreground">Demo or preview. Outbound cannot go to real inboxes.</p>
        </div>
      )}

      <SetupStrip />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border/70 bg-card/50 p-5" data-testid="admin-checklist">
          <div className="eyebrow mb-4">Readiness checklist</div>
          <div className="space-y-2">
            {ITEMS.map((it) => {
              const ok = d.checklist[it.key];
              return (
                <button key={it.key} onClick={() => navigate(it.to)} data-testid={`checklist-${it.key}`}
                  className="w-full flex items-center gap-3 rounded-lg border border-border/60 p-3 text-left hover:bg-accent/40 transition-colors">
                  <span className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${ok ? "bg-emerald-500/15 text-emerald-500" : "bg-rose-500/15 text-rose-500"}`}>
                    {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                  </span>
                  <it.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm flex-1">{it.label}</span>
                  {!ok && <span className="text-[10px] text-amber-500">set up</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-border/70 bg-card/50 p-5" data-testid="admin-plan">
            <div className="eyebrow mb-3">Plan</div>
            <div className="flex items-center justify-between">
              <span className="text-lg font-display font-semibold">{d.plan_name || "No active plan"}</span>
              <Badge className={d.live_send ? "bg-emerald-500/15 text-emerald-500 border-0" : "bg-amber-500/15 text-amber-500 border-0"} data-testid="admin-livesend-badge">
                Live send {d.live_send ? "on" : "off"}
              </Badge>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl border border-border/70 bg-card/50 p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2"><Users className="h-4 w-4" /> Seats</div>
              <div className="metric text-2xl" data-testid="admin-seats">{d.seats_used} <span className="text-sm text-muted-foreground">/ {d.seats_cap ?? "∞"}</span></div>
            </div>
            <div className="rounded-xl border border-border/70 bg-card/50 p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2"><Send className="h-4 w-4" /> Sends</div>
              <div className="metric text-2xl" data-testid="admin-sends">{d.sends_used} <span className="text-sm text-muted-foreground">/ {d.sends_quota ?? "∞"}</span></div>
            </div>
          </div>
          <button onClick={() => navigate("/app/admin/billing")} className="w-full rounded-xl border border-border/70 bg-card/50 p-4 flex items-center justify-between hover:bg-accent/40 transition-colors" data-testid="admin-billing-link">
            <span className="flex items-center gap-2 text-sm"><CreditCard className="h-4 w-4 text-amber-500" /> Billing and usage</span>
            <span className="text-xs text-muted-foreground">Open</span>
          </button>
        </div>
      </div>
    </PageBody>
  );
}
